import React from "react";
import { Link as ReactRouterLink } from "react-router-dom";
import {
    Box,
    Container,
    Heading,
    Text,
    Button,
    Link,
    VStack,
    SimpleGrid,
    Divider,
    Image,
} from "@chakra-ui/react";
import { Helmet } from "react-helmet";

export default function Home() {
    return (
        <Box bg="offwhite.100" minH="100vh">
            <Helmet>
                <title>One assessment store, manager-ready packs | BetaPersona</title>
                <meta name="description" content="After the agency shortlist you still log into GeneSys and Hogan. One store, one pack the HR or business user can use without a test-user qualification." />
            </Helmet>

            {/* Hero Section */}
            <Box bg="navy.500" color="white" py={{ base: 16, md: 24 }}>
                <Container maxW="container.xl">
                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={8} alignItems="center">
                        <VStack spacing={8} align="start">
                            <Heading as="h1" size="2xl" maxW="800px">
                                One store for the assessments. One pack for the HR or business user.
                            </Heading>
                            <Text fontSize="xl" maxW="800px">
                                You already buy Hogan/Psytech/BIOSS/SHL etc. After the agency sends a shortlist, talent and HRBP still log into publisher sites, chase email links, and then write a pack the line manager can actually use.
                            </Text>
                            <Text fontSize="xl" maxW="800px">
                                After identifying internal candidates into a cohort, talent and HRBP still log into publisher sites, chase email links, and then write a pack to motivate learning and development or succession.
                            </Text>
                            <Text fontSize="xl" maxW="800px">
                                BetaPersona sits beside those publisher platforms. We do not host rival tests to Psytech, Hogan, SHL etc. We hold the file, apply your access policy, and produce a business-ready report derived from the publisher scores and your framework.
                            </Text>
                            <Text fontSize="md" maxW="800px" opacity={0.85} fontStyle="italic">
                                Betapersona: implies - Constantly assessing the person to promote the best fit and path.
                            </Text>
                            <Box>
                                <Link
                                    as={ReactRouterLink}
                                    to="/contact"
                                    _hover={{ textDecoration: "none" }}
                                >
                                    <Button
                                        bg="accent.orange"
                                        color="white"
                                        size="lg"
                                        _hover={{ bg: "rgba(248, 100, 30, 0.85)" }}
                                    >
                                        Talk to us
                                    </Button>
                                </Link>
                                <Text mt={3} fontSize="sm">
                                    A working session with talent, HRBP or in-house occupational psychology.
                                </Text>
                            </Box>
                        </VStack>
                        
                        <Box
                            display={{ base: "none", md: "block" }}
                        >
                            <Image
                                src="/images/home-hero-8124222.jpeg"
                                alt="Professionals reviewing documents together. Photo: RDNE, Pexels"
                                borderRadius="md"
                                w="full"
                                h="auto"
                                objectFit="cover"
                                aspectRatio="16/9"
                            />
                        </Box>
                    </SimpleGrid>
                </Container>
            </Box>

            {/* What this is - four things */}
            <Container maxW="container.xl" py={{ base: 12, md: 20 }}>
                <VStack spacing={12} align="stretch">
                    <Box>
                        <Heading as="h2" size="xl" mb={8}>
                            What this is
                        </Heading>
                        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={8}>
                            <Box bg="white" p={8} borderRadius="md" shadow="sm">
                                <Heading as="h3" size="md" mb={4}>
                                    1. One repository
                                </Heading>
                                <Text>
                                    Psychometric assessments and reports in one store from selection, then 360 and performance when the person is internal or in development — managed access throughout. You can access the person's information quickly and securely. For the employer: you own the person after shortlist — not the agency, and not a folder on someone's laptop. For a practice or a coach who still runs a battery: one store across your clients.
                                </Text>
                            </Box>

                            <Box bg="white" p={8} borderRadius="md" shadow="sm">
                                <Heading as="h3" size="md" mb={4}>
                                    2. Integrations, so you stop portal-hopping
                                </Heading>
                                <Text>
                                    Live integration with your chosen publishers e.g. <Link href="https://www.psytech.com/" isExternal color="navy.500" fontWeight="600">Psytech</Link>. Candidates log into BetaPersona to see the battery allocated to them, then follow a direct authorised link out to the publisher. Fire assessments without portal-hopping or pulling PDFs by hand.
                                </Text>
                            </Box>

                            <Box bg="white" p={8} borderRadius="md" shadow="sm">
                                <Heading as="h3" size="md" mb={4}>
                                    3. A pack the HR or business user (or your client) can use
                                </Heading>
                                <Text>
                                    A business-ready report derived from those underlying publisher reports and mapped to your competency framework. The line manager does not need a psychometric or test-user qualification to read it. Nobody copy-pastes scale scores into Word.
                                </Text>
                            </Box>

                            <Box bg="white" p={8} borderRadius="md" shadow="sm">
                                <Heading as="h3" size="md" mb={4}>
                                    4. White-labelled
                                </Heading>
                                <Text>
                                    White-labelled portals for employers (TA / HRBP view), assessment practices and executive coaches who run a battery, and their end clients. The portal carries your brand, not BetaPersona.
                                </Text>
                            </Box>
                        </SimpleGrid>
                    </Box>

                    <Box bg="white" p={6} borderRadius="md" shadow="sm" fontStyle="italic">
                        <Text>
                            Years building assessment-report systems for international clients.
                        </Text>
                    </Box>

                    <Box bg="navy.500" color="white" p={8} borderRadius="md" textAlign="center">
                        <Text fontSize="xl" fontWeight="500">
                            The occupational psychologist still advises. We remove the collating.
                        </Text>
                    </Box>

                    {/* Who this is for */}
                    <Box>
                        <Heading as="h2" size="xl" mb={6}>
                            Who this is for
                        </Heading>
                        <VStack align="start" spacing={6}>
                            <Box bg="white" p={8} borderRadius="md" shadow="sm" w="full">
                                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6} alignItems="center">
                                    <Box>
                                        <Heading as="h4" size="sm" mb={3}>
                                            Employer after shortlist
                                        </Heading>
                                        <Text>
                                            Head of Talent. Head of Assessment. In-house occupational psychologist. HRBP. Head of Recruitment. L&D / talent development.
                                        </Text>
                                    </Box>
                                    <Image
                                        src="/images/employer-card-7845468.jpeg"
                                        alt="Professional at work. Photo: RDNE, Pexels"
                                        borderRadius="md"
                                        w="full"
                                        h="auto"
                                        objectFit="cover"
                                        aspectRatio="4/5"
                                    />
                                </SimpleGrid>
                            </Box>
                            <Box bg="white" p={8} borderRadius="md" shadow="sm" w="full">
                                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6} alignItems="center">
                                    <Box>
                                        <Heading as="h4" size="sm" mb={3}>
                                            Assessment practices
                                        </Heading>
                                        <Text>
                                            You already run batteries for clients. One repository across your clients, white-labelled client portals, and packs mapped to their frameworks.
                                        </Text>
                                    </Box>
                                    <Image
                                        src="/images/practice-card-12902929.jpeg"
                                        alt="Professional working at desk. Photo: Mizuno K, Pexels"
                                        borderRadius="md"
                                        w="full"
                                        h="auto"
                                        objectFit="cover"
                                        aspectRatio="4/5"
                                    />
                                </SimpleGrid>
                            </Box>
                            <Box bg="white" p={8} borderRadius="md" shadow="sm" w="full">
                                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6} alignItems="center">
                                    <Box>
                                        <Heading as="h4" size="sm" mb={3}>
                                            Executive coaches who still run a battery
                                        </Heading>
                                        <Text>
                                            Store the results, apply access policy, generate the pack. Not for coaching-only practices that run no battery.
                                        </Text>
                                    </Box>
                                    <Image
                                        src="/images/coach-card-7643735.jpeg"
                                        alt="Two professionals reviewing documents. Photo: MART PRODUCTION, Pexels"
                                        borderRadius="md"
                                        w="full"
                                        h="auto"
                                        objectFit="cover"
                                        aspectRatio="4/5"
                                    />
                                </SimpleGrid>
                            </Box>
                            <Box bg="offwhite.200" p={6} borderRadius="md" w="full" borderLeft="4px solid" borderColor="navy.500">
                                <Text fontWeight="500" mb={2}>
                                    Not for:
                                </Text>
                                <Text>
                                    High-street recruiters and CV-only agencies. Clinical psychologists. Coaching-only practices that run no battery.
                                </Text>
                            </Box>
                            <Box bg="white" p={6} borderRadius="md" w="full" fontStyle="italic">
                                <Text>
                                    Ask your HRBP or talent team to talk to us.
                                </Text>
                            </Box>
                        </VStack>
                    </Box>

                    {/* If you already have */}
                    <Box bg="white" p={8} borderRadius="md" shadow="sm">
                        <Heading as="h3" size="lg" mb={4}>
                            If you already have GeneSys, Hogan or Thomas
                        </Heading>
                        <Text mb={3}>
                            Keep them. This is an overlay: connect, store, generate. Not a rival test publisher. Not AI making up psychometrics. Pack derived from publisher scores plus your framework.
                        </Text>
                    </Box>

                    {/* What you can do in one sitting */}
                    <Box>
                        <Heading as="h2" size="xl" mb={6}>
                            What you can do in one sitting
                        </Heading>
                        <SimpleGrid columns={{ base: 1, md: 4 }} spacing={6}>
                            <Box textAlign="center">
                                <Box
                                    w={16}
                                    h={16}
                                    bg="navy.500"
                                    color="white"
                                    borderRadius="full"
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                    mx="auto"
                                    mb={4}
                                    fontSize="2xl"
                                    fontWeight="600"
                                >
                                    1
                                </Box>
                                <Text fontWeight="600">One login</Text>
                            </Box>
                            <Box textAlign="center">
                                <Box
                                    w={16}
                                    h={16}
                                    bg="navy.500"
                                    color="white"
                                    borderRadius="full"
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                    mx="auto"
                                    mb={4}
                                    fontSize="2xl"
                                    fontWeight="600"
                                >
                                    2
                                </Box>
                                <Text fontWeight="600">Fire the assessment</Text>
                            </Box>
                            <Box textAlign="center">
                                <Box
                                    w={16}
                                    h={16}
                                    bg="navy.500"
                                    color="white"
                                    borderRadius="full"
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                    mx="auto"
                                    mb={4}
                                    fontSize="2xl"
                                    fontWeight="600"
                                >
                                    3
                                </Box>
                                <Text fontWeight="600">Store it against an access policy</Text>
                            </Box>
                            <Box textAlign="center">
                                <Box
                                    w={16}
                                    h={16}
                                    bg="navy.500"
                                    color="white"
                                    borderRadius="full"
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                    mx="auto"
                                    mb={4}
                                    fontSize="2xl"
                                    fontWeight="600"
                                >
                                    4
                                </Box>
                                <Text fontWeight="600">Generate the pack for the HR or business user</Text>
                            </Box>
                        </SimpleGrid>
                    </Box>

                    {/* Three packs */}
                    <Box>
                        <Heading as="h2" size="xl" mb={6}>
                            Three packs (illustrative, not live client files)
                        </Heading>
                        <VStack align="start" spacing={6}>
                            <Box bg="white" p={8} borderRadius="md" shadow="sm" w="full">
                                <Heading as="h3" size="md" mb={4}>
                                    1. Hiring-manager pack after an agency shortlist — Recruitment
                                </Heading>
                                <Text mb={3}>
                                    The agency has already reduced the field. One document for first and second interviews: every remaining candidate against the role framework, in business English.
                                </Text>
                                <Text mb={3}>
                                    Typical inputs: ability and personality already taken in this campaign (Hogan HPI, HDS, MVPI, etc — whatever that employer already uses). BetaPersona does not add a new battery.
                                </Text>
                                <Text>
                                    The HR or business user reads it; talent, HRBP and the occupational psychologist can still open the underlying publisher reports. Side-by-side comparison and interview probes in the organisation's language. No "match percentage".
                                </Text>
                            </Box>
                            <Box bg="white" p={8} borderRadius="md" shadow="sm" w="full">
                                <Heading as="h3" size="md" mb={4}>
                                    2. Leadership selection pack — Recruitment for a senior or leadership appointment
                                </Heading>
                                <Text mb={3}>
                                    A panel needs one leadership view before they meet the last two or three people.
                                </Text>
                                <Text mb={3}>
                                    Typical inputs: a leadership-oriented personality suite already used in that process (for example Hogan HPI, HDS, MVPI), plus an ability measure already in play, etc.
                                </Text>
                                <Text>
                                    The appointment panel, Head of Talent, and the occupational psychologist read it. Strengths, pressure points, and values alignment against the employer's leadership framework. No fabricated percentiles or "readiness scores".
                                </Text>
                            </Box>
                            <Box bg="white" p={8} borderRadius="md" shadow="sm" w="full">
                                <Heading as="h3" size="md" mb={4}>
                                    3. Development pack (with 360) — Learning and development / internal only
                                </Heading>
                                <Text mb={3}>
                                    360 belongs here. Someone already in post is being developed, not selected.
                                </Text>
                                <Text mb={3}>
                                    Typical inputs: a 360, performance ratings, and psychometric scores already held (Hogan HPI, HDS, MVPI, etc — whatever that employer already uses). Not a recommended catalogue, and not MBTI as an occupational default.
                                </Text>
                                <Text>
                                    The individual and their manager read it; L&D or HRBP own the programme. Self, others, and the psychometric pattern against the same competencies. A development conversation document, not a selection recommendation.
                                </Text>
                            </Box>
                        </VStack>
                        <Box mt={6}>
                            <Link as={ReactRouterLink} to="/how-it-works" color="navy.500" fontWeight="600">
                                How it works →
                            </Link>
                        </Box>
                    </Box>

                    {/* GDPR */}
                    <Box bg="white" p={8} borderRadius="md" shadow="sm">
                        <Text mb={3}>
                            Psychometric data is special-category personal data under GDPR Article 9. We process it lawfully, store it securely, and ensure qualified test use. Data processing agreements cover storage, access and generation.
                        </Text>
                        <Link as={ReactRouterLink} to="/security-and-data" color="navy.500" fontWeight="600">
                            Security & data →
                        </Link>
                    </Box>

                    <Divider />

                    {/* End CTA */}
                    <Box bg="navy.500" color="white" p={10} borderRadius="md" textAlign="center">
                        <Heading as="h2" size="xl" mb={6}>
                            Talk to us
                        </Heading>
                        <Text fontSize="lg" mb={2}>
                            Pricing depends on volume, instruments, and whether you need the repository, the integrations, the reports, or all three. There is no public rate card. We will talk it through.
                        </Text>
                        <Text fontSize="lg" mb={8}>
                            We will walk you through the workflow, answer questions about qualified test use, and pricing. If that session shows merit, we can set up a free trial. That is the next step after the working session — not a self-serve signup.
                        </Text>
                        <Link
                            as={ReactRouterLink}
                            to="/contact"
                            _hover={{ textDecoration: "none" }}
                        >
                            <Button
                                bg="accent.orange"
                                color="white"
                                size="lg"
                                _hover={{ bg: "rgba(248, 100, 30, 0.85)" }}
                            >
                                Talk to us
                            </Button>
                        </Link>
                    </Box>
                </VStack>
            </Container>
        </Box>
    );
}
